import { FC, FormEvent, useEffect, useState } from 'react';

import { useQuery, useQueryClient } from 'react-query';

import { useChatProperty } from '@hooks_iqfu/chat/useChatProperty';
import { useOperationalNote } from '@hooks_iqfu/common/useOperationalNote';

import styles from './RightColumnMemo.module.scss';

export type OperationalNoteType = 'company' | 'chat' | 'chat_property' | 'seller' | 'sales';

type OperationalNoteMemo = {
  comment?: string;
  sales_comment?: string;
};

type Props = {
  operationalNoteType: OperationalNoteType;
  targetId: string;
  //編集不可の場合のみfalseを渡す default: true
  isEditable?: boolean;
};

const RightColumnMemo: FC<Props> = ({ operationalNoteType, targetId, isEditable = true }) => {
  const {
    getChatOperationalNote,
    updateChatOperationalNote,
    getSellerMemo,
    updateSellerMemo,
    getCompanyOperationalNote,
    updateCompanyOperationalNote,
  } = useOperationalNote();
  const { getAdminChatPropertyOperationalNote, updateAdminChatPropertyOperationalNote } = useChatProperty();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [memo, setMemo] = useState('');
  const isSalesComment = operationalNoteType === 'sales';

  const rightColumnMemoTitle = {
    chat: '売却相談メモ',
    chat_property: '物件メモ',
    seller: '売主メモ',
    company: '会社メモ',
    sales: 'セールスメモ',
  }[operationalNoteType];

  const dispatchGetOperationalNote = async (): Promise<OperationalNoteMemo | undefined> => {
    if (!targetId) return;
    switch (operationalNoteType) {
      case 'chat':
        return getChatOperationalNote({ chatRoomId: targetId });
      case 'chat_property':
        return getAdminChatPropertyOperationalNote(targetId);
      case 'seller':
        return getSellerMemo({ sellerUserId: targetId });
      case 'company':
      case 'sales':
        return getCompanyOperationalNote({ companyId: targetId });
      default:
        break;
    }
  };

  const dispatchCreateOperationalNote = async () => {
    if (!targetId) return;
    switch (operationalNoteType) {
      case 'chat':
        return await updateChatOperationalNote({ chatRoomId: targetId, payload: { comment: memo } });
      case 'chat_property':
        return await updateAdminChatPropertyOperationalNote(String(targetId), {
          comment: memo,
        });
      case 'seller':
        return await updateSellerMemo({ sellerUserId: targetId, comment: memo });
      case 'company':
        return await updateCompanyOperationalNote({
          companyId: targetId,
          comment: memo,
        });
      case 'sales':
        return await updateCompanyOperationalNote({
          companyId: targetId,
          salesComment: memo,
        });
      default:
        break;
    }
  };

  const { data: operationalNote } = useQuery(
    ['getOperationalNote', operationalNoteType, targetId],
    () => dispatchGetOperationalNote(),
    {
      enabled: targetId !== 'undefined' && !!targetId,
      staleTime: 600,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (!operationalNote) return;
    const memoText = isSalesComment ? operationalNote.sales_comment ?? '' : operationalNote?.comment ?? '';
    setMemo(memoText);
  }, [operationalNote]);

  useEffect(() => {
    setIsEditing(false);
  }, [targetId]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await dispatchCreateOperationalNote();
    setIsEditing(false);
    queryClient.invalidateQueries('getOperationalNote');
  };

  return (
    <form className={styles.right_column_container_item} onSubmit={handleSubmit}>
      <div className={styles.right_column_container_memo_area}>
        <p className={styles.right_column_container_memo_title}>{rightColumnMemoTitle}</p>
        {isEditable && !isEditing && (
          <a
            className={styles.right_column_container_memo_edit}
            onClick={() => {
              setIsEditing(true);
            }}
          >
            編集
          </a>
        )}
      </div>
      <div className={styles.right_column_container_memo_wrapper}>
        {isEditing ? (
          <textarea
            className={styles.right_column_container_memo}
            rows={20}
            placeholder="メモを追加"
            value={memo}
            maxLength={20000}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
              setMemo(event.target.value);
            }}
          />
        ) : (
          <p className={styles.right_column_container_memo}>{memo}</p>
        )}
      </div>
      {isEditing && (
        <div className={styles.right_column_container_buttons}>
          <button
            className={styles.cancel_button}
            onClick={() => {
              setIsEditing(false);
              if (!operationalNote) return;
              isSalesComment ? setMemo(operationalNote.sales_comment ?? '') : setMemo(operationalNote?.comment ?? '');
            }}
          >
            キャンセル
          </button>
          <button type="submit" className={styles.submit_button}>
            保存
          </button>
        </div>
      )}
    </form>
  );
};

export default RightColumnMemo;
